import { useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Navigate, useRoutes } from 'react-router-dom';

import BlockedPage from '~/common/components/BlockedPage';
import Center from '~/common/components/Center';
import { LeftDrawerContextProvider } from '~/common/components/Nav/LeftDrawer/context/LeftDrawerContextProvider';
import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';
import { SIGNAL_R_STATUS, USER_ROLE } from '~/common/constants';
import lang from '~/common/constants/language.json';
import useAuthentication, { useUser } from '~/common/hooks/useAuthentication';
import useSignalR from '~/common/hooks/useSignalR';
import useWorkTracking from '~/common/hooks/useWorkTracking';
import {
  generateRoleSpecificDataTrack,
  getCategoryName,
} from '~/common/util/appState';
import LoanLockModal from '~/modals/LoanTabLock/LoanTabLockModal';
import { CATEGORY_IDS } from '~/pages/Home/constants';
import boltRoutes from '~/routes';
import { useSignalRRealtime, useSignalRStore } from '~/stores/useSignalRStore';

import { FloatingMenuContainer } from './common/components/FMS/FloatingMenuSystem';
import { FeatureFlagged } from './common/components/FeatureFlagged';
import Spinner from './common/components/GlobalLayout/Spinner';
import GlobalHotkeys from './common/components/Hotkeys/GlobalHotkeys';
import useGetLoanSummary from './common/hooks/useGetLoanSummary';
import { useNavFeatureFlagsQuery } from './common/hooks/useNavFeatureFlags';
import AUSDialog from './modals/AUS/AUSDialog';
import { BoltIncidentModal } from './modals/BoltIncidentModal/BoltIncidentModal';
import { LoanCommentsContainer } from './modals/LoanComments/LoanCommentsDialog';
import { LoanContactsModalContainer } from './modals/LoanContacts/LoanContactsModal';
import LoanSummaryDialog from './modals/LoanSummaryDialog/LoanSummaryDialog';
import { classifyKeys } from './pages/ClassifyDocumentsV2/hooks/queries';
import DateTrackingContainer from './pages/DateTracking/DateTracking';
import genericPageKeys from './pages/Generic/keys';
import { HomeActions, HomeThunks } from './pages/Home/redux';

const BOLT = `
    ____        ____ 
   / __ )____  / / /_
  / __  / __ \\/ / __/
 / /_/ / /_/ / / /_  
/_____/\\____/_/\\__/                 
`;
// eslint-disable-next-line no-console
console.log(BOLT);

const App = () => {
  useWorkTracking();
  const notification = useSignalR();
  const { isAuthorized, user, isLoading } = useAuthentication();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();
  const selectedCategory = useSelector((s) => s.home.selectedCategory);
  const allCategories = useSelector((s) => s.home.navigationItems.categories);
  const { onInit, shouldGetNextAlert } = useSelector(({ home }) => home);
  const latestSignalRNotification = useSignalRStore(
    (s) => s.latestSignalRNotification,
  );
  const conditionCategoryToWaitFor = useSignalRStore(
    (s) => s.conditionCategoryToWaitFor,
  );
  const setConditionCategoryToWaitFor = useSignalRStore(
    (s) => s.setConditionCategoryToWaitFor,
  );
  const setLatestSignalRNotification = useSignalRStore(
    (s) => s.setLatestSignalRNotification,
  );
  const loanNumber = useGetLoanSummary().data?.loanInfo?.loanNumber;

  useSignalRRealtime({
    notifications: SIGNAL_R_STATUS.CLASSIFY_STATUS_CHANGE,
    isEnabled: isAuthorized && selectedCategory !== CATEGORY_IDS.CLASSIFY,
    onMessage: () => {
      dispatch(
        HomeThunks.getNavigationDetails(
          true,
          [user.role].flat().includes(USER_ROLE.UNDERWRITER),
        ),
      );
      queryClient.invalidateQueries({
        queryKey: [classifyKeys.all, classifyKeys.classifyDocuments],
      });
    },
  });

  const debouncedGetNavigationDetailsRef = useRef(
    debounce(() => {
      dispatch(
        HomeThunks.getNavigationDetails(
          true,
          [user?.role].flat().includes(USER_ROLE.UNDERWRITER),
        ),
      );
    }, 2000),
  );

  const debouncedInvalidateYamlCachesRef = useRef(
    debounce(() => {
      queryClient.invalidateQueries({ queryKey: genericPageKeys.all });
    }, 1000),
  );

  useEffect(() => {
    if (
      (latestSignalRNotification?.notificationType ===
        SIGNAL_R_STATUS.RULE_EVALUATION_COMPLETED &&
        (conditionCategoryToWaitFor === null ||
          latestSignalRNotification.conditionCategory ===
            conditionCategoryToWaitFor)) ||
      latestSignalRNotification?.notificationType ===
        SIGNAL_R_STATUS.REQUEST_COMPLETED
    ) {
      setConditionCategoryToWaitFor(null);
      debouncedGetNavigationDetailsRef.current();
      debouncedInvalidateYamlCachesRef.current();
    } else if (
      (latestSignalRNotification?.notificationType ===
        SIGNAL_R_STATUS.RULE_EVALUATION_ERROR ||
        latestSignalRNotification?.notificationType ===
          SIGNAL_R_STATUS.REQUEST_ERRORED) &&
      shouldGetNextAlert &&
      !onInit
    ) {
      dispatch(HomeActions.setShouldGetNextAlert(false));
      setConditionCategoryToWaitFor(null);
      showSnackbar({
        message: lang.DEFAULT_OOPS_TITLE,
        severity: 'error',
        duration: 5000,
      });
    }
  }, [
    conditionCategoryToWaitFor,
    dispatch,
    latestSignalRNotification,
    onInit,
    setConditionCategoryToWaitFor,
    shouldGetNextAlert,
    showSnackbar,
  ]);

  useEffect(() => {
    if (loanNumber && !document.title.includes(loanNumber)) {
      document.title = `${loanNumber} | BOLT`;
    }
  }, [loanNumber]);

  // update redux store on new signalR notification
  useEffect(() => {
    if (isEmpty(notification)) {
      return;
    }
    setLatestSignalRNotification(notification);
  }, [notification, setLatestSignalRNotification]);

  useEffect(() => {
    if (!user || !globalThis.dataTracking) {
      return;
    }
    globalThis.dataTracking.tab = getCategoryName(selectedCategory);

    generateRoleSpecificDataTrack(
      allCategories,
      [user.role].flat().includes(USER_ROLE.UNDERWRITER),
    );
  }, [user, allCategories, selectedCategory]);

  if (isLoading) {
    return (
      <Center width="100%" height="100%">
        <Spinner />
      </Center>
    );
  }

  if (!isAuthorized) {
    return <BlockedPage />;
  }

  return (
    <LeftDrawerContextProvider>
      <BrowserRouter>
        <Routes />
        <LoanLockModal />
        <LoanCommentsContainer />
        <DateTrackingContainer />
        <LoanContactsModalContainer />
        <BoltIncidentModal />
        <LoanSummaryDialog />
        <AUSDialog />
        <FeatureFlagged featureFlag="IsFloatingMenuEnabled">
          <FloatingMenuContainer allCategories={allCategories} />
        </FeatureFlagged>
        <FeatureFlagged featureFlag="IsHotkeysEnabled">
          <GlobalHotkeys />
        </FeatureFlagged>
      </BrowserRouter>
    </LeftDrawerContextProvider>
  );
};

function Routes() {
  const {
    isLoading,
    data: { isAuditHistoryRouteEnabled },
  } = useNavFeatureFlagsQuery();
  const { isUnderwriter } = useUser();

  const routes = useMemo(() => {
    let filteredRoutes = boltRoutes;
    if (!isLoading && !isAuditHistoryRouteEnabled) {
      filteredRoutes = filteredRoutes.filter(
        (route) => !route.isAuditHistoryRouteEnabled,
      );
    }

    if (!isUnderwriter) {
      return [
        ...filteredRoutes.filter(({ isUnderwriterOnly }) => !isUnderwriterOnly),
        {
          path: '/:lenderDatabaseId/:loanRecordId/know-your-loan/:category?',
          element: <Navigate to=".." replace relative="path" />,
        },
      ];
    }
    return filteredRoutes;
  }, [isUnderwriter, isAuditHistoryRouteEnabled, isLoading]);

  return useRoutes(routes);
}

export default App;
